import React, { useMemo, useState, useEffect } from 'react';
import Avatar from '../../common/Avatar';
import { getDescriptionExcerpt, getTextColorForBg } from '../../../utils/textUtils';
import { formatDistanceToNow } from 'date-fns';
import { zhTW } from 'date-fns/locale';

const PRIORITY_MAP = {
  4: { label: '緊急', className: 'p4' },
  3: { label: '高', className: 'p3' },
  2: { label: '中', className: 'p2' },
  1: { label: '低', className: 'p1' },
};

const TicketTable = ({
  tickets,
  loading,
  sortBy,
  sortOrder,
  handleSort,
  onRowClick,
  statusOptions,
  userMap,
  usersMapByEmail,
  showProject = true,
  emptyState,
}) => {
  // 每 60 秒 tick 一次，讓相對時間文字自動更新
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  const statusMap = useMemo(() => {
    const map = {};
    (statusOptions || []).forEach((s) => {
      map[s.value] = s;
    });
    return map;
  }, [statusOptions]);

  const getStatusInfo = (value) =>
    statusMap[value] || { value, label: value, color: '#94a3b8' };

  const SortIcon = ({ field }) => {
    const isActive = sortBy === field;
    return (
      <span className={`sort-icon ${isActive ? 'active' : ''}`}>
        {isActive ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="list-table-wrapper">
        <table className="list-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Ticket</th>
              <th style={{ width: 90 }}>狀態</th>
              <th style={{ width: 70 }}>優先度</th>
              <th style={{ width: 180 }}>指派人員</th>
              <th style={{ width: 100 }}>截止日</th>
              <th style={{ width: 140 }}>最後更新</th>
              {showProject && <th style={{ width: 120 }}>專案</th>}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="list-row skeleton-row">
                <td><div className="skeleton skeleton-id" /></td>
                <td>
                  <div className="skeleton skeleton-title" />
                  <div className="skeleton skeleton-desc" />
                </td>
                <td><div className="skeleton skeleton-pill" /></td>
                <td><div className="skeleton skeleton-pill" /></td>
                <td><div className="skeleton skeleton-avatar" /></td>
                <td><div className="skeleton skeleton-pill" /></td>
                <td><div className="skeleton skeleton-pill" /></td>
                {showProject && <td><div className="skeleton skeleton-tag" /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tickets.length === 0) {
    return emptyState || null;
  }

  return (
    <div className="list-table-wrapper">
      <table className="list-table">
        <thead>
          <tr>
            <th
              className="col-id sortable"
              onClick={() => handleSort('ticket_no')}
              style={{ width: 60 }}
            >
              # <SortIcon field="ticket_no" />
            </th>
            <th className="col-title">Ticket</th>
            <th
              className="col-status sortable"
              onClick={() => handleSort('status')}
              style={{ width: 90 }}
            >
              狀態 <SortIcon field="status" />
            </th>
            <th
              className="col-priority sortable"
              onClick={() => handleSort('priority')}
              style={{ width: 70 }}
            >
              優先度 <SortIcon field="priority" />
            </th>
            <th className="col-assignees" style={{ width: 180 }}>
              指派人員
            </th>
            <th
              className="col-end-date sortable"
              onClick={() => handleSort('end_date')}
              style={{ width: 100 }}
            >
              截止日 <SortIcon field="end_date" />
            </th>
            <th
              className="col-last-activity sortable"
              onClick={() => handleSort('last_activity_at')}
              style={{ width: 140 }}
            >
              最後更新 <SortIcon field="last_activity_at" />
            </th>
            {showProject && (
              <th className="col-project" style={{ width: 120 }}>
                專案
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => {
            const si = getStatusInfo(ticket.status);
            const pi = PRIORITY_MAP[ticket.priority] || null;
            return (
              <tr
                key={ticket.id}
                className="list-row"
                onClick={() => onRowClick(ticket)}
              >
                <td className="col-id">
                  <span className="ticket-id">#{ticket.ticket_no}</span>
                </td>
                <td className="col-title">
                  <div className="title-cell">
                    <span className="ticket-title">{ticket.title}</span>
                    {ticket.description && (
                      <span className="ticket-desc">
                        {getDescriptionExcerpt(ticket.description)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="col-status">
                  <span
                    className="status-pill"
                    style={{ backgroundColor: si.color, color: getTextColorForBg(si.color) }}
                  >
                    {si.label}
                  </span>
                </td>
                <td className="col-priority">
                  {pi && ticket.priority > 0 ? (
                    <span className={`priority-badge ${pi.className}`}>
                      {pi.label}
                    </span>
                  ) : null}
                </td>
                <td className="col-assignees">
                  {(() => {
                    const assignees = ticket.ticket_assignees || [];
                    if (assignees.length === 0) return null;
                    const a = assignees[0];
                    const email = a.profiles?.email?.toLowerCase();
                    const resolved = email ? usersMapByEmail[email] : null;
                    const user = resolved || userMap[a.user_id];
                    const name = user?.name || a.profiles?.email || '未知';
                    return (
                      <div className="assignee-list">
                        <div className="assignee-item">
                          <Avatar
                            avatarUrl={user?.avatar_url}
                            name={name}
                            size="small"
                          />
                          <span className="assignee-name">{name}</span>
                          {assignees.length > 1 && (
                            <span className="more-assignees">+{assignees.length - 1}</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className="col-end-date">
                  {ticket.end_date && (
                    <span className={`end-date-label${ticket.end_date < new Date().toISOString().split('T')[0] && ticket.status !== 'closed' && ticket.status !== 'completed' ? ' overdue' : ''}`}>
                      {ticket.end_date}
                    </span>
                  )}
                </td>
                <td className="col-last-activity">
                  {ticket.last_activity_at && (() => {
                    const activityUser = ticket.last_activity_by ? userMap[ticket.last_activity_by] : null;
                    const isNew = ticket.has_new_activity;
                    return (
                      <div className={`last-activity-cell${isNew ? ' unread' : ''}`}>
                        <span className="last-activity-time">
                          {formatDistanceToNow(new Date(ticket.last_activity_at), {
                            addSuffix: true,
                            locale: zhTW,
                          })}
                        </span>
                        {activityUser && (
                          <span className="last-activity-user" title={activityUser.name}>
                            {activityUser.name}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                {showProject && (
                  <td className="col-project">
                    {ticket.projects?.name && (
                      <span className="project-tag">
                        <span
                          className="project-dot-sm"
                          style={{
                            backgroundColor: ticket.projects?.color || '#667eea',
                          }}
                        />
                        {ticket.projects.name}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TicketTable;
